import { useEffect, useState } from 'react';
import { Link, useLocation, matchPath } from 'react-router-dom';
import {
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Box,
  Typography,
  Menu,
  MenuItem,
  Divider
} from '@mui/material';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import NavItem from './NavItem';
import { useGetMenuMaster } from 'api/menu';
import { getNavIconBoxSx, NAV } from '../../drawerNavStyles';

const miniMenuPaperSx = {
  minWidth: 200,
  mt: 0.5,
  ml: 0.75,
  borderRadius: 2.5,
  border: '1px solid rgba(31, 91, 64, 0.1)',
  boxShadow: '0 8px 24px rgba(15, 45, 32, 0.12)'
};

export default function NavCollapse({ item, level = 1 }) {
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster?.isDashboardDrawerOpened;
  const { pathname } = useLocation();

  const isChildActive = item.children?.some((child) =>
    matchPath({ path: child?.link || child.url, end: false }, pathname)
  );
  const [open, setOpen] = useState(isChildActive || false);
  const [miniMenuAnchor, setMiniMenuAnchor] = useState(null);
  const miniMenuOpen = Boolean(miniMenuAnchor);

  const Icon = item.icon;
  const isActive = isChildActive;

  useEffect(() => {
    setMiniMenuAnchor(null);
  }, [pathname]);

  const handleMiniMenuClose = () => setMiniMenuAnchor(null);

  if (!drawerOpen) {
    return (
      <Box sx={{ py: 0.5, display: 'flex', justifyContent: 'center' }}>
        {Icon && (
          <Box
            role="button"
            tabIndex={0}
            aria-label={`${item.title} menu`}
            aria-haspopup="menu"
            aria-expanded={miniMenuOpen}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setMiniMenuAnchor(e.currentTarget);
              }
            }}
            sx={{
              ...getNavIconBoxSx(isActive),
              cursor: 'pointer',
              '&:hover': { bgcolor: isActive ? NAV.iconBgActive : NAV.hover }
            }}
            onClick={(e) => setMiniMenuAnchor(e.currentTarget)}
          >
            <Icon style={{ fontSize: '1.125rem' }} />
          </Box>
        )}
        <Menu
          anchorEl={miniMenuAnchor}
          open={miniMenuOpen}
          onClose={handleMiniMenuClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{ paper: { sx: miniMenuPaperSx } }}
        >
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              px: 2,
              pt: 1.25,
              pb: 0.75,
              fontWeight: 600,
              fontSize: '0.75rem',
              color: 'text.secondary',
              letterSpacing: '0.04em',
              textTransform: 'uppercase'
            }}
          >
            {item.title}
          </Typography>
          <Divider sx={{ mb: 0.5 }} />
          {item.children?.map((child) => {
            const ChildIcon = child.icon;
            const childSelected = !!matchPath(
              { path: child?.link || child.url, end: false },
              pathname
            );
            return (
              <MenuItem
                key={child.id}
                component={Link}
                to={child.url}
                selected={childSelected}
                onClick={handleMiniMenuClose}
                sx={{
                  py: 1,
                  px: 2,
                  fontSize: '0.875rem',
                  fontWeight: childSelected ? 600 : 500,
                  color: childSelected ? 'primary.main' : 'text.primary',
                  '&.Mui-selected': {
                    bgcolor: NAV.activeSoft,
                    '&:hover': { bgcolor: 'rgba(31, 91, 64, 0.14)' }
                  }
                }}
              >
                {ChildIcon && (
                  <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                    <ChildIcon style={{ fontSize: '1rem' }} />
                  </ListItemIcon>
                )}
                <ListItemText primary={child.title} />
              </MenuItem>
            );
          })}
        </Menu>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 0.25 }}>
      <ListItemButton
        onClick={() => setOpen(!open)}
        sx={{
          borderRadius: NAV.radius,
          mx: NAV.mx,
          mb: 0.5,
          py: 0.75,
          px: 1,
          minHeight: NAV.itemHeight,
          transition: 'all 0.2s ease',
          bgcolor: isActive ? NAV.activeSoft : 'transparent',
          '&:hover': { bgcolor: isActive ? 'rgba(31, 91, 64, 0.14)' : NAV.hover }
        }}
      >
        {Icon && (
          <ListItemIcon sx={{ minWidth: NAV.iconSize + 8, mr: 1.25 }}>
            <Box sx={getNavIconBoxSx(false)}>
              <Icon style={{ fontSize: '1.125rem' }} />
            </Box>
          </ListItemIcon>
        )}
        <ListItemText
          primary={
            <Typography
              variant="body2"
              sx={{
                fontWeight: isActive ? 600 : 500,
                color: isActive ? 'primary.main' : 'text.primary',
                fontSize: '0.875rem',
                letterSpacing: '-0.01em'
              }}
            >
              {item.title}
            </Typography>
          }
        />
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: open ? 'rgba(31, 91, 64, 0.1)' : 'rgba(31, 91, 64, 0.05)',
            transition: 'all 0.2s ease'
          }}
        >
          <KeyboardArrowDownRoundedIcon
            sx={{
              fontSize: '1.125rem',
              color: isActive ? 'primary.main' : 'text.secondary',
              transition: 'transform 0.25s ease',
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)'
            }}
          />
        </Box>
      </ListItemButton>

      <Collapse in={open} timeout={280} unmountOnExit>
        <Box
          sx={{
            ml: 2.5,
            mr: NAV.mx,
            pl: 1.5,
            borderLeft: `2px solid ${NAV.submenuBorder}`,
            mb: 0.5
          }}
        >
          <List disablePadding>
            {item.children?.map((child) => (
              <NavItem key={child.id} item={child} level={level + 1} />
            ))}
          </List>
        </Box>
      </Collapse>
    </Box>
  );
}
